import { getAccessToken } from '../auth/msal';
import { config } from '../config';
import type {
  CreateFormRequest,
  CreateSecretLinkRequest,
  AuditEvent,
  SubmissionListItem,
  SubmissionDetail,
  EmailConfig,
  EmbedConfig,
  FormDetail,
  FormSummary,
  SecretLink,
  SecretLinkCreated,
  UpdateDraftRequest,
  UpdateEmailConfigRequest,
  UpdateEmbedConfigRequest,
  UpdateWebhookRequest,
  WebhookConfig,
  WebhookDelivery,
} from './types';

async function request<T>(path: string, method: string, body?: unknown): Promise<T> {
  const token = await getAccessToken();
  const response = await fetch(`${config.apiBaseUrl}${path}`, {
    method,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (!response.ok) {
    const detail = await response.text();
    throw new ApiError(response.status, detail || response.statusText);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export const formsApi = {
  list: () => request<FormSummary[]>('/api/forms', 'GET'),
  get: (id: string) => request<FormDetail>(`/api/forms/${id}`, 'GET'),
  create: (body: CreateFormRequest) => request<FormDetail>('/api/forms', 'POST', body),
  updateDraft: (id: string, body: UpdateDraftRequest) =>
    request<FormDetail>(`/api/forms/${id}/draft`, 'PUT', body),
  publish: (id: string) => request<FormDetail>(`/api/forms/${id}/publish`, 'POST'),
  createDraft: (id: string) => request<FormDetail>(`/api/forms/${id}/draft`, 'POST'),
  listSecretLinks: (id: string) => request<SecretLink[]>(`/api/forms/${id}/secret-links`, 'GET'),
  createSecretLink: (id: string, body: CreateSecretLinkRequest) =>
    request<SecretLinkCreated>(`/api/forms/${id}/secret-links`, 'POST', body),
  revokeSecretLink: (id: string, linkId: string) =>
    request<void>(`/api/forms/${id}/secret-links/${linkId}/revoke`, 'POST'),
  getWebhook: (id: string) => request<WebhookConfig>(`/api/forms/${id}/webhook`, 'GET'),
  updateWebhook: (id: string, body: UpdateWebhookRequest) =>
    request<WebhookConfig>(`/api/forms/${id}/webhook`, 'PUT', body),
  listWebhookDeliveries: (id: string) =>
    request<WebhookDelivery[]>(`/api/forms/${id}/webhook/deliveries`, 'GET'),
  getNotifications: (id: string) => request<EmailConfig>(`/api/forms/${id}/notifications`, 'GET'),
  updateNotifications: (id: string, body: UpdateEmailConfigRequest) =>
    request<EmailConfig>(`/api/forms/${id}/notifications`, 'PUT', body),
  getEmbed: (id: string) => request<EmbedConfig>(`/api/forms/${id}/embed`, 'GET'),
  updateEmbed: (id: string, body: UpdateEmbedConfigRequest) =>
    request<EmbedConfig>(`/api/forms/${id}/embed`, 'PUT', body),
  getAudit: (id: string) => request<AuditEvent[]>(`/api/forms/${id}/audit`, 'GET'),
  listSubmissions: (id: string) => request<SubmissionListItem[]>(`/api/forms/${id}/submissions`, 'GET'),
  getSubmission: (id: string, submissionId: string) =>
    request<SubmissionDetail>(`/api/forms/${id}/submissions/${submissionId}`, 'GET'),
  submissionPdf: async (id: string, submissionId: string): Promise<Blob> => {
    const token = await getAccessToken();
    const response = await fetch(`${config.apiBaseUrl}/api/forms/${id}/submissions/${submissionId}/pdf`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) {
      throw new ApiError(response.status, (await response.text()) || response.statusText);
    }
    return response.blob();
  },
  submissionFile: async (id: string, submissionId: string, fileId: string): Promise<Blob> => {
    const token = await getAccessToken();
    const response = await fetch(
      `${config.apiBaseUrl}/api/forms/${id}/submissions/${submissionId}/files/${fileId}`,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    if (!response.ok) {
      throw new ApiError(response.status, (await response.text()) || response.statusText);
    }
    return response.blob();
  },
  exportSubmissions: async (id: string): Promise<Blob> => {
    const token = await getAccessToken();
    const response = await fetch(`${config.apiBaseUrl}/api/forms/${id}/submissions/export`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) {
      throw new ApiError(response.status, (await response.text()) || response.statusText);
    }
    return response.blob();
  },
  exportVersion: async (id: string, versionId: string): Promise<Blob> => {
    const token = await getAccessToken();
    const response = await fetch(`${config.apiBaseUrl}/api/forms/${id}/versions/${versionId}/export`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) {
      throw new ApiError(response.status, (await response.text()) || response.statusText);
    }
    return response.blob();
  },
};
